import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";

import type { VisualizationAssetSettings } from "@/shared/domain/urdf";

import styles from "./SettingsStep.module.css";
import {
  orientsToViewerAxes,
  withViewerAxisOrientation,
} from "./viewerOrientation";

interface SettingsStepProps {
  settings: VisualizationAssetSettings;
  onChange: (settings: VisualizationAssetSettings) => void;
  /** An error from the request itself, as opposed to from the conversion. */
  error?: string;
}

/**
 * What to convert, and how.
 *
 * Nearly empty by default: the common case is "just ingest my robot", and every
 * knob has a defensible default. Only the collision/visual choice is on the form
 * itself — it decides what the user will be looking at — and the rest live
 * behind *Advanced* so the form does not present a page of decisions to someone
 * who has none to make.
 *
 * Several of the converter's options are not offered at all. `output_format` is
 * forced to the two-file `separate` form, because the viewer resolves a glTF's
 * sibling `.bin` and a packed file would defeat that; `naming` and the
 * destination are the platform's, since the artifact lands at a canonical path;
 * `include_viewer` is moot because this dialog *is* the viewer; and
 * `articulation` is always on — a robot converted without its kinematic tree
 * cannot be posed, which is the thing the preview exists to do. `articulation`
 * is still sent explicitly, from {@link DEFAULT_VISUALIZATION_ASSET_SETTINGS},
 * rather than left to the request model's own default: both are `true` today,
 * and a wizard that silently followed the backend if that changed would be
 * ingesting un-poseable robots with no sign of it here.
 */
export function SettingsStep({ settings, onChange, error }: SettingsStepProps) {
  const set = <K extends keyof VisualizationAssetSettings>(
    key: K,
    value: VisualizationAssetSettings[K],
  ) => {
    onChange({ ...settings, [key]: value });
  };

  return (
    <div className={styles.settingsStep}>
      <p className={styles.intro}>
        Convert this robot description into a viewable 3D asset. Referenced
        meshes are resolved from this dataset.
      </p>
      {error && <Alert severity="error">{error}</Alert>}
      <FormControlLabel
        control={
          <Switch
            checked={settings.collision}
            onChange={(event) => set("collision", event.target.checked)}
          />
        }
        label="Use collision geometry instead of visual geometry"
      />
      <Accordion className={styles.advanced} disableGutters elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Advanced
        </AccordionSummary>
        <AccordionDetails className={styles.advancedDetails}>
          {/* The wire field is `z_up`, which means the opposite of what this
              says — see `viewerOrientation`, which owns the inversion. */}
          <div className={styles.field}>
            <FormControlLabel
              control={
                <Switch
                  checked={orientsToViewerAxes(settings)}
                  onChange={(event) =>
                    onChange(
                      withViewerAxisOrientation(settings, event.target.checked),
                    )
                  }
                />
              }
              label="Orient the asset to Roboto's viewer axis orientation"
            />
            <p className={styles.fieldHelp}>
              Preview framing assumes Roboto&apos;s orientation. Turn this off
              and the model keeps its own axes, but the preview camera will not
              frame it sensibly.
            </p>
          </div>
          <FormControlLabel
            control={
              <Switch
                checked={settings.strict}
                onChange={(event) => set("strict", event.target.checked)}
              />
            }
            label="Fail if any referenced mesh cannot be resolved"
          />
          <TextField
            fullWidth
            helperText="Extra package roots for resolving package:// references, as name=path pairs."
            label="Packages"
            onChange={(event) => set("packages", event.target.value)}
            size="small"
            value={settings.packages}
          />
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
